import React from "react";
import { cn } from "../../lib/utils";

/* Layout ------------------------------------------------------------------ */
export function Container({ className, children }) {
  return <div className={cn("container-x", className)}>{children}</div>;
}

export function Section({ className, alt = false, children, id }) {
  return (
    <section
      id={id}
      className={cn("py-16 sm:py-20 lg:py-24", alt && "bg-secondary/40 border-y border-border", className)}
    >
      {children}
    </section>
  );
}

export function Eyebrow({ children, className }) {
  return <span className={cn("eyebrow", className)}>{children}</span>;
}

export function SectionHead({ eyebrow, title, subtitle, center, className }) {
  return (
    <div className={cn("max-w-2xl", center && "mx-auto text-center", className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {title && <h2 className="mt-4 text-3xl sm:text-4xl">{title}</h2>}
      {subtitle && <p className="mt-4 text-base leading-relaxed text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

/* Card -------------------------------------------------------------------- */
export function Card({ className, children, ...props }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card text-card-foreground shadow-sm",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/* Badge ------------------------------------------------------------------- */
const badgeTones = {
  neutral: "bg-secondary text-secondary-foreground border-border",
  brand: "bg-brand/12 text-brand border-brand/30",
  exams: "bg-exams/12 text-exams border-exams/30",
  ctf: "bg-ctf/12 text-ctf border-ctf/30",
  success: "bg-success/12 text-success border-success/30",
  warning: "bg-warning/15 text-warning border-warning/30",
  danger: "bg-destructive/12 text-destructive border-destructive/30",
};

export function Badge({ tone = "neutral", className, children, dot = false }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        badgeTones[tone],
        className
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/* Form controls (full-width by global convention) ------------------------- */
export function Field({ label, hint, children, className }) {
  return (
    <label className={cn("block w-full", className)}>
      {label && <span className="mb-1.5 block text-sm font-medium text-foreground">{label}</span>}
      {children}
      {hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

const controlBase =
  "w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus:border-brand focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0";

export const Input = React.forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn(controlBase, "h-11", className)} {...props} />;
});

export const Textarea = React.forwardRef(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn(controlBase, "min-h-[110px] resize-y", className)} {...props} />;
});

export const Select = React.forwardRef(function Select({ className, children, ...props }, ref) {
  return (
    <select ref={ref} className={cn(controlBase, "h-11 appearance-none pr-9", className)} {...props}>
      {children}
    </select>
  );
});
